import { PlusOutlined } from '@ant-design/icons';
import { DrawerHeaderAction, SharedEmpty, SharedTable } from '@components';
import { useView } from '@hooks';
import { OrderWorkLog } from '@services';
import { formatLoggedTime } from '@utils';
import { Button, Space, Statistic, Table, Tag, Typography } from 'antd';
import React from 'react';
import { Columns, isAssignee } from './OrderWorkLogs.columns';
import OrderWorkLogForm from './OrderWorkLogForm';
import { IOrderWorkLogsProps } from './OrderWorkLogs.props';

const { Text } = Typography;

const OrderWorkLogs: React.FC<IOrderWorkLogsProps> = ({
  order,
  workLogsHook,
  locked,
  canManage,
  currentUserId,
  canEdit,
  editing,
  formOpen,
  openCreate,
  openEdit,
  closeForm,
  handleDelete,
}) => {
  const { isMobile } = useView();
  const { loading, dataSource, hasOpen, totalMinutes, minutesByTechnician, minutesByService } =
    workLogsHook;

  const columns = Columns({ order, isMobile, canEdit, openEdit, handleDelete });

  // One empty state for both layouts, with the action in it: a blank table is not an invitation.
  const emptyState = (
    <SharedEmpty
      description={
        canManage ? 'No time logged on this job yet' : 'No time logged on this job yet - add your own'
      }
      action={
        !locked && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Log time
          </Button>
        )
      }
    />
  );

  const summary = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? 16 : 32,
        alignItems: 'flex-start',
        marginBottom: 16,
      }}
    >
      <Statistic
        title={hasOpen ? 'Total logged (incl. running)' : 'Total logged'}
        value={formatLoggedTime(totalMinutes)}
      />
      {minutesByTechnician.length > 0 && (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            By technician
          </Text>
          <Space size={[4, 4]} wrap>
            {minutesByTechnician.map((t) => (
              <Tag key={t.key} color={isAssignee(t.key, order) ? 'blue' : undefined}>
                {t.label}: {formatLoggedTime(t.minutes)}
              </Tag>
            ))}
          </Space>
        </div>
      )}
      {minutesByService.length > 0 && (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            By service
          </Text>
          <Space size={[4, 4]} wrap>
            {minutesByService.map((s) => (
              <Tag key={s.key ?? 'whole-job'}>
                {s.label}: {formatLoggedTime(s.minutes)}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </div>
  );

  const logTimeButton = (
    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
      Log time
    </Button>
  );

  return (
    <>
      {/* The primary action lives in the drawer header; the summary is the body's own header. With
          no drawer around (the build page's Work logs tab) it sits right-aligned above the list. */}
      {!locked && (
        <DrawerHeaderAction
          inline={
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              {logTimeButton}
            </div>
          }
        >
          {logTimeButton}
        </DrawerHeaderAction>
      )}

      {dataSource.length > 0 && summary}

      {!loading && dataSource.length === 0 ? (
        emptyState
      ) : isMobile ? (
        // SharedTable is the app's mobile list: it lays each row out as a card from the columns.
        <SharedTable
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          totalCount={dataSource.length}
          maxResultCount={dataSource.length || 1}
          enableRowClick={!locked}
          onRowClick={(log: OrderWorkLog) => canEdit(log) && openEdit(log)}
        />
      ) : (
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
        />
      )}

      <OrderWorkLogForm
        open={formOpen}
        order={order}
        editing={editing}
        currentUserId={currentUserId}
        canManage={canManage}
        onClose={closeForm}
      />
    </>
  );
};

export default OrderWorkLogs;
